/**
 * wav.js — 16 kHz mono 16-bit PCM WAV, encoded in the browser.
 *
 * Deliberately NOT MediaRecorder: Chrome emits webm/opus and iOS Safari emits
 * mp4/aac, so the backend would have to branch per browser or transcode. We
 * capture raw samples through the Web Audio API instead and write the 44-byte
 * canonical WAV header by hand — identical bytes from every device, no
 * dependency, no server-side conversion.
 *
 * Roughly 32 kB per second (16000 × 2), so the 60s cap is ~1.9 MB worst case.
 */

export const TARGET_SAMPLE_RATE = 16000;
export const WAV_HEADER_BYTES = 44;

/** Flatten the ScriptProcessor's per-block Float32Arrays into one buffer. */
export function mergeChunks(chunks) {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Float32Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

/**
 * Box-average downsample to `targetRate`. Averaging the source window rather
 * than picking one sample out of it is the cheapest anti-alias that keeps
 * speech intelligible at a 3:1 ratio (48 kHz → 16 kHz is the common case).
 */
export function downsample(input, inputRate, targetRate = TARGET_SAMPLE_RATE) {
  if (!inputRate || inputRate <= targetRate) return input;

  const ratio = inputRate / targetRate;
  const outLength = Math.floor(input.length / ratio);
  const out = new Float32Array(outLength);

  for (let i = 0; i < outLength; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.min(Math.floor((i + 1) * ratio), input.length);
    let sum = 0;
    let n = 0;
    for (let j = start; j < end; j++) {
      sum += input[j];
      n += 1;
    }
    out[i] = n ? sum / n : 0;
  }

  return out;
}

/** Loudest absolute sample, 0..1. Drives the silence gate and the level meter. */
export function peakAmplitude(samples) {
  let peak = 0;
  for (let i = 0; i < samples.length; i++) {
    const v = samples[i] < 0 ? -samples[i] : samples[i];
    if (v > peak) peak = v;
  }
  return peak;
}

const writeAscii = (view, offset, text) => {
  for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
};

/**
 * @param {Float32Array} samples  mono, -1..1, already at `sampleRate`
 * @returns {ArrayBuffer} a complete RIFF/WAVE file
 */
export function encodeWav(samples, sampleRate = TARGET_SAMPLE_RATE) {
  const bitsPerSample = 16;
  const channels = 1;
  const bytesPerSample = bitsPerSample / 8;
  const dataBytes = samples.length * bytesPerSample;
  const buffer = new ArrayBuffer(WAV_HEADER_BYTES + dataBytes);
  const view = new DataView(buffer);

  writeAscii(view, 0, "RIFF");
  view.setUint32(4, 36 + dataBytes, true); // everything after this field
  writeAscii(view, 8, "WAVE");

  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true); // PCM fmt chunk length
  view.setUint16(20, 1, true); // audioFormat: 1 = PCM
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * channels * bytesPerSample, true); // byteRate
  view.setUint16(32, channels * bytesPerSample, true); // blockAlign
  view.setUint16(34, bitsPerSample, true);

  writeAscii(view, 36, "data");
  view.setUint32(40, dataBytes, true);

  let offset = WAV_HEADER_BYTES;
  for (let i = 0; i < samples.length; i++) {
    // Clamp before scaling: a sample above 1 would wrap to a loud negative.
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }

  return buffer;
}

export function wavBlob(samples, sampleRate = TARGET_SAMPLE_RATE) {
  return new Blob([encodeWav(samples, sampleRate)], { type: "audio/wav" });
}
